import React, { useState } from "react";
import { Pdi, apiErrorMessage } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Wrench } from "lucide-react";
import { toast } from "sonner";

const VIN_RE = /^[A-Z0-9]{17}$/;
const ENGINE_LEN = 12;
const ENGINE_RE = new RegExp(`^[A-Z0-9]{${ENGINE_LEN}}$`);

/**
 * AssignVinButton — opens a popup to capture VIN + Engine No on a released
 * sale that came in WITHOUT them. Saving calls the existing PDI release
 * endpoint, which validates both fields, completes the PDI_VIN task, and
 * spawns the downstream Accounts task.
 *
 * Props:
 *   saleId, userId        — required
 *   currentVin, currentEngine — pre-fill values (rare; usually empty)
 *   onSaved()             — called with the updated sale on success
 *   size, variant         — passed through to the trigger button
 */
export default function AssignVinButton({
  saleId, userId,
  currentVin = "", currentEngine = "",
  onSaved,
  size = "sm",
  variant = "outline",
  testId = "assign-vin-btn",
}) {
  const [open, setOpen] = useState(false);
  const [vin, setVin] = useState(currentVin || "");
  const [eng, setEng] = useState(currentEngine || "");
  const [saving, setSaving] = useState(false);

  const reset = () => { setVin(currentVin || ""); setEng(currentEngine || ""); };

  const vinError = vin && !VIN_RE.test(vin) ? "VIN must be 17 alphanumeric characters" : null;
  const engError = eng && !ENGINE_RE.test(eng)
    ? `Engine Number must be exactly ${ENGINE_LEN} alphanumeric characters.`
    : null;
  const canSave = VIN_RE.test(vin) && ENGINE_RE.test(eng) && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const updated = await Pdi.release(saleId, {
        vin: vin.trim().toUpperCase(),
        engine_no: eng.trim().toUpperCase(),
        user_id: userId,
      });
      toast.success("VIN assigned · sale released for Accounts");
      setOpen(false);
      onSaved?.(updated);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to assign VIN");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}
    >
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className="gap-1.5"
                data-testid={testId}>
          <Wrench className="h-4 w-4" />
          Assign VIN
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary" /> Assign VIN &amp; Engine No
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Both fields are required. Saving will mark VIN Assignment complete
            and release the sale to Accounts for invoicing.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="assign-vin-input">VIN (Chassis Number) *</Label>
            <Input
              id="assign-vin-input"
              value={vin}
              onChange={(e) => setVin(e.target.value.toUpperCase())}
              maxLength={17}
              placeholder="MJYRG2515P0000000"
              autoFocus
              autoComplete="off"
              className="font-mono-data"
              data-testid="assign-vin-input"
            />
            {vinError && <p className="text-xs text-rose-600">{vinError}</p>}
            <p className="text-[11px] text-slate-500">
              Exactly 17 alphanumeric characters.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="assign-engine-input">Engine Number *</Label>
            <Input
              id="assign-engine-input"
              value={eng}
              onChange={(e) => setEng(e.target.value.toUpperCase())}
              maxLength={12}
              placeholder="E25KP0000000"
              autoComplete="off"
              className="font-mono-data"
              data-testid="assign-engine-input"
            />
            {engError && <p className="text-xs text-rose-600">{engError}</p>}
            <p className="text-[11px] text-slate-500">
              Exactly 12 alphanumeric characters.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}
                  data-testid="assign-vin-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSave}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="assign-vin-save">
            {saving ? "Saving…" : "Save & Release"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
